import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import useSound from "use-sound";
import { unsettledBetsAPI } from "../../service/AuthService";
import ping from "../../assets/sound/ping.mp3";

const getBetSide = (bet) => (bet?.back_rate || bet?.back_run ? "BACK" : "LAY");

export default function CurrentBets() {
  const [activeTab, setActiveTab] = useState("SPORTS");
  const [betStatus, setBetStatus] = useState("MATCHED");
  const [betSide, setBetSide] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const latestBetDate = useRef(null);
  const [play] = useSound(ping);

  const sendNotification = useCallback((bet) => {
    if (!bet || latestBetDate.current === bet.created_date) return;
    if (latestBetDate.current && Notification.permission === "granted") {
      new Notification("New Bet Placed", {
        body: `Bet Placed on ${bet.event_name} by ${bet.userName}`,
      });
      play();
    }
    latestBetDate.current = bet.created_date;
  }, [play]);

  const loadBets = useCallback(async () => {
    setLoading(true);
    const result = await unsettledBetsAPI({
      betStatus,
      betType: betSide,
      gameType: activeTab,
      isCasino: activeTab === "CASINO",
      pageNumber: page,
      pageSize: size,
      ...(search.trim() ? { search: search.trim() } : {}),
    });
    const response = result?.response || {};
    const content = Array.isArray(response) ? response : response?.content || [];
    const normalizedRows = content.map((item) => ({
      ...item,
      betSide: getBetSide(item),
      userRate: item.back_rate ?? item.lay_rate ?? item.back_run ?? item.lay_run ?? 0,
    }));
    setRows(normalizedRows);
    setTotalElements(Number(response?.totalElements ?? normalizedRows.length));
    setTotalPages(Number(response?.totalPages ?? (normalizedRows.length ? 1 : 0)));
    sendNotification(normalizedRows[0]);
    setLoading(false);
  }, [activeTab, betSide, betStatus, page, search, sendNotification, size]);

  useEffect(() => {
    loadBets();
    const interval = setInterval(loadBets, 8000);
    return () => clearInterval(interval);
  }, [loadBets]);

  const visibleRows = useMemo(() => (
    betSide === "ALL" ? rows : rows.filter((row) => row.betSide === betSide)
  ), [betSide, rows]);

  const totalAmount = useMemo(() => (
    visibleRows.reduce((total, row) => total + Number(row?.amount || 0), 0)
  ), [visibleRows]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    setPage(0);
  };

  return (
    <div className="main current-bets-page">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 main-container">
            <div className="listing-grid">
              <div className="detail-row"><h2>Current Bets</h2></div>

              <div className="current-bets-tabs" role="tablist">
                <button type="button" className={activeTab === "SPORTS" ? "active" : ""} onClick={() => selectTab("SPORTS")}>Sports</button>
                <button type="button" className={activeTab === "CASINO" ? "active" : ""} onClick={() => selectTab("CASINO")}>Casino</button>
              </div>

              <div className="current-bets-panel">
                <div className="current-bets-filter-row">
                  <div className="current-bets-radio-groups">
                    <div className="current-bets-radio-line">
                      <label><input type="radio" name="current-bet-status" value="MATCHED" checked={betStatus === "MATCHED"} onChange={(event) => setBetStatus(event.target.value)} />Matched</label>
                      <label><input type="radio" name="current-bet-status" value="DELETED" checked={betStatus === "DELETED"} onChange={(event) => setBetStatus(event.target.value)} />Deleted</label>
                    </div>
                    <div className="current-bets-radio-line">
                      {[["ALL", "All"], ["BACK", "Back"], ["LAY", "Lay"]].map(([value, label]) => (
                        <label key={value}><input type="radio" name="current-bet-side" value={value} checked={betSide === value} onChange={(event) => setBetSide(event.target.value)} />{label}</label>
                      ))}
                      <button type="button" className="btn btn-diamond current-bets-load" disabled={loading} onClick={() => { setPage(0); loadBets(); }}>{loading ? "Loading..." : "Load"}</button>
                    </div>
                  </div>
                  <div className="current-bets-totals">
                    <span>Total Soda: {totalElements}</span>
                    <span>Total Amount: {totalAmount.toFixed(2)}</span>
                  </div>
                </div>

                <div className="current-bets-table-tools">
                  <label>Show <select value={size} onChange={(event) => { setSize(Number(event.target.value)); setPage(0); }}><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option></select> entries</label>
                  <label className="current-bets-search">Search:<input type="search" className="form-control" name="current-bets-search" autoComplete="off" placeholder="Search..." value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} /></label>
                </div>

                <div className="table-responsive data-table">
                  <table className="table table-bordered datatable dataTable current-bets-table">
                    <thead><tr><th>Event Type</th><th>Event Name</th><th>User Name</th><th>M Name</th><th>Nation</th><th className="current-bets-number">User Rate</th><th className="current-bets-number">Amount</th><th>Place Date</th></tr></thead>
                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr><td colSpan={8} className="dataTables_empty">{loading ? "Loading bets..." : "No data available in table"}</td></tr>
                      ) : visibleRows.map((item, index) => (
                        <tr key={item.betId || item.id || `${item.created_date}-${index}`} className={item.betSide === "BACK" ? "back-bet-row" : "lay-bet-row"}>
                          <td>{item.event_type}</td><td>{item.event_name}</td><td>{item.userName}</td><td>{item.marketName}</td><td>{item.runner_name}</td><td className="current-bets-number">{item.userRate}</td><td className="current-bets-number">{item.amount}</td>
                          <td>{item.created_date ? moment(`${item.created_date}Z`).utc().local().format("DD/MM/YYYY HH:mm:ss") : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="current-bets-pagination">
                  <button type="button" disabled={page === 0} onClick={() => setPage(0)}>«</button>
                  <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>‹</button>
                  <span>{page + 1}</span>
                  <button type="button" disabled={totalPages === 0 || page + 1 >= totalPages} onClick={() => setPage(page + 1)}>›</button>
                  <button type="button" disabled={totalPages === 0 || page + 1 >= totalPages} onClick={() => setPage(totalPages - 1)}>»</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
